/**
 * InfinitSales - Navigation Sidebar Component
 * Collapsible navigation with icon-only mode
 * 
 * UPDATED: Added channel switcher (Evolution vs WA Business)
 */
import React, { useState, useEffect } from 'react';
import {
  Kanban,
  ScrollText,
  Megaphone,
  Bot,
  History,
  FileText,
  User,
  RefreshCw,
  Wifi,
  Sun,
  Moon,
  LogOut,
  Smartphone,
  Building2,
  CreditCard,
  Zap,
  Calendar,
  Instagram,
  Facebook,
  Globe,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import NotificationBell from './notifications/NotificationBell';
import WhatsAppIcon from './icons/WhatsAppIcon';
import InstagramAccountModal from './instagram/InstagramAccountModal';
import instagramService from '../services/instagram/instagramService';
import MessengerAccountModal from './messenger/MessengerAccountModal';
import messengerService from '../services/messenger/messengerService';
import widgetService from '../services/widget/widgetService';
import { useNavigate, useLocation } from 'react-router-dom';
import { getDelegation, endDelegation } from '../utils/delegation';

// Ultimo status conhecido do canal Instagram, compartilhado entre montagens do
// NavSidebar (cada troca de rota remonta o trilho): so evita o item do canal
// piscar ate a resposta chegar. A validade (60s) e a dedup do GET vivem no
// requestCache, dentro de instagramService.getStatus, que o shell do inbox
// tambem chama: a troca de aba faz UM GET /instagram/status, nao dois. 404 e o
// caminho normal de canal desligado (flag off) e vira `data: null`.
let igStatusCache = null; // { data: object|null }

// Mesmo racional para o canal Messenger (messengerService.getStatus).
let msStatusCache = null; // { data: object|null }

// Assinantes do status (os setState dos NavSidebar montados). O shell do inbox
// publica o status FRESCO logo após conectar uma conta — sem isso o ícone do
// canal só surgia depois do TTL de 60s ou de um reload (o disconnect já
// invalidava o cache por morar no NavSidebar; o connect acontece em outro
// componente e não tinha como avisar). Também cobre a corrida do redirect do
// OAuth, em que o getStatus do NavSidebar resolvia `connected:false` antes da
// troca do code terminar.
const igStatusSubs = new Set();
export function publishInstagramStatus(data) {
  igStatusCache = { data };
  igStatusSubs.forEach((fn) => { try { fn(data); } catch { /* noop */ } });
}
const msStatusSubs = new Set();
export function publishMessengerStatus(data) {
  msStatusCache = { data };
  msStatusSubs.forEach((fn) => { try { fn(data); } catch { /* noop */ } });
}
// Widget do site (5o canal): mesmo racional.
let wdStatusCache = null; // { data: object|null }
const wdStatusSubs = new Set();
export function publishWidgetStatus(data) {
  wdStatusCache = { data };
  wdStatusSubs.forEach((fn) => { try { fn(data); } catch { /* noop */ } });
}

const NavSidebar = ({
  currentPage,
  onPageChange,
  onRefresh,
  onOpenConnection,
  // Mobile: quando um chat está aberto em tela cheia, a barra inferior some
  // (igual WhatsApp/Instagram, que escondem a bottom-nav dentro da conversa).
  mobileHideNav = false,
}) => {
  const { colors, theme, toggleTheme } = useTheme();
  const { user, logout, isCompanyAdmin, isCompanyMember } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showChannelMenu, setShowChannelMenu] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  // Canal Instagram: o item do trilho e o "Conexão Instagram" do menu só
  // aparecem quando o backend responde 200 no /status (INSTAGRAM_ENABLED).
  const [igStatus, setIgStatus] = useState(() => (igStatusCache ? igStatusCache.data : null));
  const [showIgAccount, setShowIgAccount] = useState(false);

  // Sempre passa pelo service: dentro do TTL o requestCache responde na hora
  // (sem GET) e, em voo, compartilha a promise com o shell do inbox.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await instagramService.getStatus();
        const data = res?.data || null;
        igStatusCache = { data };
        if (!cancelled) setIgStatus(data);
      } catch (e) {
        // 404 = flag desligada; erro de rede não é cacheado para tentar de
        // novo na próxima montagem.
        if (e?.response?.status === 404) {
          igStatusCache = { data: null };
        }
        if (!cancelled) setIgStatus(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Recebe o status publicado pelo shell após conectar (o ícone aparece na
  // hora). Registra/desregistra o próprio setter para não vazar setState em
  // componente desmontado.
  useEffect(() => {
    igStatusSubs.add(setIgStatus);
    return () => { igStatusSubs.delete(setIgStatus); };
  }, []);

  const igEnabled = !!igStatus;

  // Canal Messenger: mesmo padrão do Instagram (item do trilho + "Conexão
  // Messenger" só com o backend respondendo 200 no /status).
  const [msStatus, setMsStatus] = useState(() => (msStatusCache ? msStatusCache.data : null));
  const [showMsAccount, setShowMsAccount] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await messengerService.getStatus();
        const data = res?.data || null;
        msStatusCache = { data };
        if (!cancelled) setMsStatus(data);
      } catch (e) {
        if (e?.response?.status === 404) {
          msStatusCache = { data: null };
        }
        if (!cancelled) setMsStatus(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    msStatusSubs.add(setMsStatus);
    return () => { msStatusSubs.delete(setMsStatus); };
  }, []);

  const msEnabled = !!msStatus;

  // 5o canal (widget do site, 2026-09-23): mesmo padrão dos canais da Meta —
  // item do trilho e "Conexão Widget" só com o backend respondendo 200 no
  // /status (WIDGET_ENABLED). O widget não tem modal de conta: a conexão é a
  // página de cards (/widget/conexoes), com N widgets por workspace.
  const [wdStatus, setWdStatus] = useState(() => (wdStatusCache ? wdStatusCache.data : null));
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await widgetService.getStatus();
        const data = res?.data || null;
        wdStatusCache = { data };
        if (!cancelled) setWdStatus(data);
      } catch (e) {
        if (e?.response?.status === 404) {
          wdStatusCache = { data: null };
        }
        if (!cancelled) setWdStatus(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    wdStatusSubs.add(setWdStatus);
    return () => { wdStatusSubs.delete(setWdStatus); };
  }, []);
  const wdEnabled = !!wdStatus;

  // No mobile (<768px) a navegação "deita": vira uma barra inferior (bottom
  // tabs) em vez do trilho lateral de 68px.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Determine active channel from URL
  const activeChannel = location.pathname.startsWith('/wa-business') ? 'wa_business' : 'evolution';

  // Sessão delegada (dentro do workspace de outra pessoa): o avatar ganha um
  // anel âmbar como sinalizador permanente, e itens PESSOAIS do menu (Minha
  // Conta, Agentes de IA) somem — pertencem ao dono do workspace, não a quem
  // está operando dentro dele.
  const delegation = getDelegation();
  const isDelegatedSession = !!delegation;

  // Channel definitions
  const channels = [
    { 
      id: 'evolution', 
      label: 'WhatsApp Local', 
      description: 'Conexão via QR Code',
      icon: Smartphone, 
      color: '#25D366',
      path: '/inbox'
    },
    { 
      id: 'wa_business', 
      label: 'WhatsApp Business', 
      description: 'Conexão via Meta',
      icon: Building2, 
      color: '#1877f2',
      path: '/wa-business'
    },
  ];

  const currentChannelData = channels.find(c => c.id === activeChannel) || channels[0];

  const styles = {
    container: isMobile
      ? {
          // Bottom tabs: barra horizontal fixa na base.
          position: 'fixed',
          left: 0, right: 0, bottom: 0,
          width: '100%',
          height: '58px',
          backgroundColor: colors.bgPrimary,
          borderTop: `1px solid ${colors.borderColor}`,
          display: mobileHideNav ? 'none' : 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-around',
          zIndex: 100,
          // Respeita o safe-area dos iPhones (notch inferior).
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
        }
      : {
          width: '68px',
          minWidth: '68px',
          height: '100vh',
          backgroundColor: colors.bgPrimary,
          borderRight: `1px solid ${colors.borderColor}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: '12px',
        },
    // Channel Switcher
    channelSwitcher: {
      position: 'relative',
      marginBottom: '8px',
    },
    channelButton: {
      width: '46px',
      height: '46px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'all 0.15s',
      position: 'relative',
    },
    channelButtonInner: {
      width: '38px',
      height: '38px',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
    },
    channelIndicator: {
      position: 'absolute',
      bottom: '2px',
      right: '2px',
      width: '12px',
      height: '12px',
      borderRadius: '50%',
      backgroundColor: colors.bgPrimary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    channelMenu: {
      position: 'absolute',
      top: '0',
      left: '56px',
      backgroundColor: colors.bgPrimary,
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
      padding: '8px',
      zIndex: 1000,
      border: `1px solid ${colors.borderColor}`,
      minWidth: '220px',
    },
    channelMenuHeader: {
      padding: '8px 12px',
      fontSize: '11px',
      fontWeight: 600,
      color: colors.textTertiary,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    channelMenuItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      width: '100%',
      padding: '10px 12px',
      border: 'none',
      backgroundColor: 'transparent',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'background-color 0.15s',
      textAlign: 'left',
    },
    channelMenuItemActive: {
      backgroundColor: `${colors.accentPrimary}15`,
    },
    channelMenuItemHover: {
      backgroundColor: colors.bgTertiary,
    },
    channelMenuIcon: {
      width: '36px',
      height: '36px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    channelMenuInfo: {
      flex: 1,
      minWidth: 0,
    },
    channelMenuLabel: {
      fontSize: '14px',
      fontWeight: 500,
      color: colors.textPrimary,
      marginBottom: '2px',
    },
    channelMenuDesc: {
      fontSize: '12px',
      color: colors.textSecondary,
    },
    channelMenuCheck: {
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    // Navigation
    nav: isMobile
      ? {
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-around',
          gap: '2px',
          flex: 1,
          height: '100%',
          padding: 0,
          margin: 0,
        }
      : {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px',
          width: '100%',
          padding: '0 10px',
          marginTop: '8px',
        },
    navItem: {
      width: '46px',
      height: '46px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      backgroundColor: 'transparent',
      color: colors.textSecondary,
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'all 0.15s',
      position: 'relative',
    },
    navItemActive: {
      backgroundColor: `${colors.accentPrimary}20`,
      color: colors.accentPrimary,
    },
    navItemHover: {
      backgroundColor: colors.bgTertiary,
    },
    navItemDot: {
      position: 'absolute',
      left: '0px',
      width: '4px',
      height: '20px',
      backgroundColor: colors.accentPrimary,
      borderRadius: '0 4px 4px 0',
    },
    tooltip: {
      position: 'absolute',
      left: '60px',
      backgroundColor: colors.bgTertiary,
      color: colors.textPrimary,
      padding: '8px 12px',
      borderRadius: '8px',
      fontSize: '13px',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      zIndex: 1000,
      boxShadow: colors.shadowLg,
      pointerEvents: 'none',
    },
    divider: {
      width: '32px',
      height: '1px',
      backgroundColor: colors.borderColor,
      margin: '8px 0',
    },
    // Bolinha vermelha de "nova notificacao" sobre o sino. footerBtn ja tem
    // position relative, entao ancora no canto superior direito do botao.
    notificationDot: {
      position: 'absolute',
      top: '8px',
      right: '8px',
      width: '9px',
      height: '9px',
      borderRadius: '50%',
      backgroundColor: '#ef4444',
      border: `2px solid ${colors.bgPrimary}`,
    },
    footer: isMobile
      ? {
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-around',
          gap: '2px',
          padding: 0,
          height: '100%',
        }
      : {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px',
          padding: '12px 10px',
          width: '100%',
        },
    footerBtn: {
      width: '46px',
      height: '46px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      backgroundColor: 'transparent',
      color: colors.textSecondary,
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'all 0.15s',
      position: 'relative',
    },
    userAvatar: {
      width: '38px',
      height: '38px',
      borderRadius: '50%',
      backgroundColor: colors.bgTertiary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.textSecondary,
      cursor: 'pointer',
      overflow: 'hidden',
      border: `2px solid transparent`,
      transition: 'border-color 0.15s',
    },
    userAvatarActive: {
      borderColor: colors.accentPrimary,
    },
    userMenu: {
      position: isMobile ? 'fixed' : 'absolute',
      // Mobile: o avatar está na barra SUPERIOR, então o menu desce a partir
      // dela, ancorado à esquerda. Desktop: sobe a partir do rodapé do trilho.
      top: isMobile ? '56px' : 'auto',
      bottom: isMobile ? 'auto' : '60px',
      left: isMobile ? '8px' : '68px',
      right: 'auto',
      maxHeight: isMobile ? '75vh' : 'none',
      overflowY: isMobile ? 'auto' : 'visible',
      backgroundColor: colors.bgPrimary,
      borderRadius: '12px',
      boxShadow: colors.shadowLg,
      padding: '8px 0',
      zIndex: 1001,
      border: `1px solid ${colors.borderColor}`,
      minWidth: '220px',
    },
    // Barra superior do mobile (acima do input de busca): perfil à esquerda,
    // marca no centro, sino à direita.
    mobileTopBar: {
      position: 'fixed',
      top: 0, left: 0, right: 0,
      height: '54px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
      padding: '0 10px',
      backgroundColor: colors.bgPrimary,
      borderBottom: `1px solid ${colors.borderColor}`,
      zIndex: 100,
    },
    // Marca da plataforma (barra superior): logo em container branco circular +
    // wordmark, padronizado com a tela de login. No tema CLARO o container ganha
    // sombra/borda porque o fundo da logo é branco e some no fundo claro.
    brand: {
      flex: 1,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 8,
      userSelect: 'none',
      minWidth: 0,
    },
    brandLogo: {
      width: 30, height: 30, borderRadius: 8,
      backgroundColor: '#ffffff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden', flexShrink: 0,
      boxShadow: theme === 'dark' ? 'none' : '0 1px 4px rgba(0,0,0,0.18)',
      border: theme === 'dark' ? 'none' : `1px solid ${colors.borderColor}`,
    },
    brandText: {
      fontSize: '17px', fontWeight: 700, letterSpacing: '0.2px',
      color: colors.textPrimary,
      fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif",
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    },
    userMenuHeader: {
      padding: '12px 16px',
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    userName: {
      fontSize: '14px',
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '2px',
    },
    userEmail: {
      fontSize: '12px',
      color: colors.textSecondary,
    },
    userMenuItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      width: '100%',
      padding: '12px 16px',
      border: 'none',
      backgroundColor: 'transparent',
      color: colors.textPrimary,
      fontSize: '14px',
      cursor: 'pointer',
      textAlign: 'left',
      transition: 'background-color 0.15s',
    },
    userMenuItemDanger: {
      color: '#ef4444',
    },
    userMenuDivider: {
      height: '1px',
      backgroundColor: colors.borderColor,
      margin: '8px 0',
    },
  };

  // Conta Empresa: admin gets a top-level "Empresa" entry point. Member loses
  // "Agentes" (managed by admin via shared agent) — listing here would invite
  // a 403 from the backend mutating endpoints, which is bad UX. GET endpoints
  // remain open so autopilot still works internally.
  // Formularios Meta segue restrito enquanto o recurso esta em beta, mas o
  // recorte e uma CAPACIDADE do workspace (`meta_leads_enabled`, vinda do
  // perfil em GET /api/users/me), nao uma lista de ids de cliente no codigo:
  // hardcode de cliente viola a regra 4.1 do CLAUDE.md (D19 do sweep 2B).
  const canSeeMetaLeads = Boolean(user?.meta_leads_enabled);

  // Tipo de conexao da conta (Meta Business vs QR). Define quais itens do menu
  // de perfil aparecem: Templates da Meta (business) x Atualizar conversas (QR).
  const isBusinessChannel = user?.default_channel === 'wa_business';

  // Navegacao principal (topo da sidebar). Itens menos frequentes (Mensagens
  // Prontas, Templates, Agentes) foram movidos para o menu de perfil (bolinha).
  // Item do canal no trilho: com conta CONECTADA sempre; sem conta, só quando
  // o backend disser `always_visible` (CHANNELS_ALWAYS_VISIBLE_ENABLED) e o
  // usuário puder conectar (admin, solo, membro `own`). Sem isso, quem nunca
  // conectou não sabia que o canal existe: a conta de teste do dono mostrava
  // só WhatsApp e Facebook porque as contas de Instagram dela estão
  // desconectadas. O item leva para a página do canal, que abre a conexão.
  const canalVisivel = (st) => !!st && (st.connected || (st.always_visible && st.can_connect));
  const igVisivel = canalVisivel(igStatus);
  const msVisivel = canalVisivel(msStatus);
  const wdVisivel = canalVisivel(wdStatus);

  const navItems = [
    // Etapa 2: admin E membro acessam /empresa. Admin cai no painel de gestão;
    // membro cai na grade de workspaces (pool). O EmpresaDashboard decide a view.
    ...((isCompanyAdmin || isCompanyMember) ? [
      { id: 'empresa', label: 'Empresa', icon: Building2, route: '/empresa' },
    ] : []),
    // Alternador de inboxes, na ordem WhatsApp, Instagram, Facebook. O item
    // do inbox (WhatsApp) vem primeiro e vira "logo"; Instagram e Messenger
    // seguem a regra do `canalVisivel` acima. O rótulo do WhatsApp só muda
    // para "WhatsApp" quando divide o trilho com outro canal.
    { id: 'inbox', label: (igVisivel || msVisivel || wdVisivel) ? 'WhatsApp' : 'Conversas', icon: WhatsAppIcon },
    ...(igVisivel ? [
      { id: 'instagram', label: 'Instagram', icon: Instagram, route: '/instagram' },
    ] : []),
    ...(msVisivel ? [
      { id: 'messenger', label: 'Messenger', icon: Facebook, route: '/messenger' },
    ] : []),
    // 5o canal: o widget do site. Sem widget criado, leva para a pagina de
    // conexoes (cards), onde o snippet nasce.
    ...(wdVisivel ? [
      { id: 'widget', label: 'Widget', icon: Globe, route: wdStatus?.connected ? '/widget' : '/widget/conexoes' },
    ] : []),
    // Sem `route` de proposito: onde o shell monta a pagina embutida o clique
    // fica no shell (ver ROTAS_DE_PAGINA_INTERNA e destinoDoItem).
    { id: 'tags-kanban', label: 'Funis', icon: Kanban },
    // Campanhas dispara onPageChange('campaigns') e cada shell intercepta no
    // handlePageChange para fazer navigate('/campaigns'), evitando flicker.
    { id: 'campaigns', label: 'Campanhas', icon: Megaphone },
    { id: 'followups', label: 'Follow-ups', icon: Zap },
    // Calendário do workspace (eventos nativos + espelho Google Calendar).
    { id: 'calendar', label: 'Agenda', icon: Calendar, route: '/calendar' },
    ...(canSeeMetaLeads ? [
      { id: 'meta-leads', label: 'Formulários Meta', icon: FileText },
    ] : []),
  ];

  // Navega para itens do menu de perfil que vivem fora do shell atual.
  // Espelha o padrao das paginas standalone: rota propria quando existe,
  // senao entra no shell do canal (wa-business/inbox) via state.targetPage.
  const goToPage = (pageId) => {
    setShowUserMenu(false);
    const standaloneRoutes = {
      'saved-messages': '/saved-messages',
      'meta-leads': '/meta-leads',
      'campaigns': '/campaigns',
      'empresa': '/empresa',
      // 'agents' é CORE, não do canal. Sem rota própria ele caía no fallback
      // abaixo e EJETAVA quem estava no Instagram/Messenger para o shell do
      // WhatsApp — os únicos dois que montam AgentsPage internamente.
      'agents': '/agents',
      // Funis e Follow-ups (D6 do sweep 2B): aqui a rota propria vale sempre,
      // porque `goToPage` e o menu de perfil, que nao tem shell nenhum para
      // montar a pagina embutida. No trilho a escolha e por capacidade do
      // shell atual (ver destinoDoItem).
      'tags-kanban': '/funis',
      'followups': '/followups',
    };
    if (standaloneRoutes[pageId]) {
      navigate(standaloneRoutes[pageId]);
      return;
    }
    navigate(isBusinessChannel ? '/wa-business' : '/inbox', {
      state: { targetPage: pageId },
    });
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showUserMenu && !e.target.closest('.nav-user-container')) {
        setShowUserMenu(false);
      }
      if (showChannelMenu && !e.target.closest('.nav-channel-container')) {
        setShowChannelMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showUserMenu, showChannelMenu]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleRefresh = () => {
    setShowUserMenu(false);
    onRefresh?.();
  };

  const handleOpenConnection = () => {
    setShowUserMenu(false);
    onOpenConnection?.();
  };

  const handleToggleTheme = () => {
    toggleTheme();
  };

  // Paginas CORE que os shells de WhatsApp montam EMBUTIDAS (`renderPageContent`),
  // e que fora deles precisam da rota propria.
  const ROTAS_DE_PAGINA_INTERNA = {
    'tags-kanban': '/funis',
    'followups': '/followups',
  };

  // O criterio e a CAPACIDADE do shell atual de montar a pagina, nao o canal:
  // /inbox e /wa-business montam o Kanban e os follow-ups embutidos (e o
  // onOpenChat embutido ainda cria o pseudo-chat para contato sem conversa),
  // entao ali o clique continua no shell via onPageChange. Em qualquer outro
  // lugar (Instagram, Messenger, paginas standalone) nao ha o que montar, e
  // sem a rota propria o operador era ejetado para o shell do WhatsApp com
  // state.targetPage (D6 do sweep 2B).
  const shellMontaPaginaInterna = location.pathname.startsWith('/inbox')
    || location.pathname.startsWith('/wa-business');
  const destinoDoItem = (item) => item.route
    || (shellMontaPaginaInterna ? null : ROTAS_DE_PAGINA_INTERNA[item.id])
    || null;

  // Render compartilhado entre o grupo primario (topo) e o grupo de
  // configuracao (rodape) para manter active/hover/tooltip identicos.
  const renderNavButton = (item) => {
    const Icon = item.icon;
    // Items that own a route (like Empresa, which lives outside the
    // inbox shell) are flagged active by matching the URL prefix.
    // Inbox-internal items keep using the `currentPage` prop.
    const isActive = item.route
      ? location.pathname.startsWith(item.route)
      : currentPage === item.id;
    const isHovered = hoveredItem === item.id;

    // Items with `route` navigate directly; others toggle the active
    // page inside the inbox shell via onPageChange, ou vao para a rota
    // propria quando o shell atual nao monta a pagina (destinoDoItem).
    const destino = destinoDoItem(item);
    const handleClick = destino
      ? () => navigate(destino)
      : () => onPageChange(item.id);

    // No mobile cada item vira uma "aba": ícone em cima, rótulo pequeno embaixo,
    // sem indicador lateral nem tooltip; o ativo é sinalizado só pela cor.
    if (isMobile) {
      return (
        <button
          key={item.id}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: '3px', flex: 1, height: '100%',
            border: 'none', background: 'transparent', cursor: 'pointer',
            color: isActive ? colors.accentPrimary : colors.textSecondary,
            padding: '4px 2px',
          }}
          onClick={handleClick}
        >
          <Icon size={22} />
          <span style={{
            fontSize: '10px', fontWeight: isActive ? 700 : 500,
            lineHeight: 1, whiteSpace: 'nowrap',
          }}>
            {item.label}
          </span>
        </button>
      );
    }

    return (
      <button
        key={item.id}
        // O trilho do desktop e so icone: o rotulo aparece no tooltip, que e
        // desenhado por nos. O aria-label da o mesmo nome a quem navega por
        // leitor de tela (e e o alvo estavel do script de captura de telas).
        aria-label={item.label}
        style={{
          ...styles.navItem,
          ...(isActive ? styles.navItemActive : {}),
          ...(!isActive && isHovered ? styles.navItemHover : {}),
        }}
        onClick={handleClick}
        onMouseEnter={() => setHoveredItem(item.id)}
        onMouseLeave={() => setHoveredItem(null)}
      >
        {isActive && <div style={styles.navItemDot} />}
        <Icon size={22} />
        {isHovered && (
          <div style={styles.tooltip}>{item.label}</div>
        )}
      </button>
    );
  };

  // No mobile a barra inferior fica enxuta: só Empresa, Conversas e Etiquetas.
  // O restante da navegação (Campanhas, Follow-ups, etc.) migra para o menu do
  // perfil, aberto pela barra SUPERIOR.
  const MOBILE_BOTTOM_IDS = ['empresa', 'instagram', 'messenger', 'widget', 'inbox', 'tags-kanban'];
  const bottomItems = navItems.filter((i) => MOBILE_BOTTOM_IDS.includes(i.id));
  const restItems = navItems.filter((i) => !MOBILE_BOTTOM_IDS.includes(i.id));

  // Dropdown do perfil — reutilizado no rodapé (desktop) e na barra superior
  // (mobile). No mobile recebe os itens de navegação que saíram da barra de
  // baixo. Sem toggle de tema no mobile (o tema segue o nativo do aparelho).
  const renderUserMenu = () => (
    <div
      style={{
        ...styles.userMenu,
        ...(isDelegatedSession
          ? { border: '2px solid #f59e0b', boxShadow: '0 0 0 1px #f59e0b40, ' + (colors.shadowLg || '0 8px 30px rgba(0,0,0,0.3)') }
          : {}),
      }}
    >
      <div
        style={{
          ...styles.userMenuHeader,
          ...(isDelegatedSession
            ? (theme === 'dark'
                ? { background: 'linear-gradient(135deg, rgba(245,158,11,0.30) 0%, rgba(245,158,11,0.10) 100%)' }
                : { backgroundColor: 'rgba(245,158,11,0.18)' })
            : {}),
        }}
      >
        {isDelegatedSession ? (
          <>
            <div style={{ ...styles.userEmail, color: '#f59e0b', fontWeight: 600, marginBottom: 2 }}>
              Você está acessando o workspace de
            </div>
            <div style={styles.userName}>
              {delegation?.targetName || 'outro usuário'}
            </div>
          </>
        ) : (
          <>
            <div style={styles.userName}>{user?.name || 'Usuário'}</div>
            <div style={styles.userEmail}>{user?.email}</div>
          </>
        )}
      </div>
      {isDelegatedSession && (
        <button
          style={{ ...styles.userMenuItem, color: '#f59e0b', fontWeight: 600 }}
          onClick={() => {
            const d = getDelegation();
            if (d?.actorUserId) {
              try { sessionStorage.setItem(`autoworkspace-tried-${d.actorUserId}`, '1'); } catch {}
            }
            endDelegation();
            window.location.href = '/empresa';
          }}
        >
          <LogOut size={16} />
          Sair do workspace
        </button>
      )}

      {/* Mobile: itens de navegação que saíram da barra inferior. */}
      {isMobile && restItems.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.id}
            style={styles.userMenuItem}
            onClick={() => {
              setShowUserMenu(false);
              // Mesma regra do trilho: rota propria quando o shell atual nao
              // monta a pagina embutida.
              const destino = destinoDoItem(item);
              if (destino) navigate(destino); else onPageChange(item.id);
            }}
          >
            <Icon size={16} />
            {item.label}
          </button>
        );
      })}
      {isMobile && <div style={styles.userMenuDivider} />}

      {!isDelegatedSession && (
        <button style={styles.userMenuItem} onClick={() => { setShowUserMenu(false); navigate('/account'); }}>
          <CreditCard size={16} />
          Minha Conta
        </button>
      )}
      {(isCompanyAdmin || isCompanyMember) && (
        <button style={styles.userMenuItem} onClick={() => { setShowUserMenu(false); navigate('/empresa'); }}>
          <Building2 size={16} />
          Minha Empresa
        </button>
      )}
      <button style={styles.userMenuItem} onClick={() => goToPage('saved-messages')}>
        <History size={16} />
        Mensagens Prontas
      </button>
      {!isCompanyMember && !isDelegatedSession && (
        <button style={styles.userMenuItem} onClick={() => goToPage('agents')}>
          <Bot size={16} />
          Agentes de IA
        </button>
      )}

      <div style={styles.userMenuDivider} />

      <button style={styles.userMenuItem} onClick={handleOpenConnection}>
        <Wifi size={16} />
        Conexão WhatsApp
      </button>
      {igEnabled && (igStatus.connected || igStatus.can_connect !== false) && (
        <button
          style={styles.userMenuItem}
          onClick={() => { setShowUserMenu(false); setShowIgAccount(true); }}
        >
          <Instagram size={16} />
          Conexão Instagram
        </button>
      )}
      {msEnabled && (msStatus.connected || msStatus.can_connect !== false) && (
        <button
          style={styles.userMenuItem}
          onClick={() => { setShowUserMenu(false); setShowMsAccount(true); }}
        >
          <Facebook size={16} />
          Conexão Messenger
        </button>
      )}
      {wdEnabled && (wdStatus.connected || wdStatus.can_connect !== false) && (
        <button
          style={styles.userMenuItem}
          onClick={() => { setShowUserMenu(false); navigate('/widget/conexoes'); }}
        >
          <Globe size={16} />
          Conexão Widget
        </button>
      )}
      {!isBusinessChannel && (
        <button style={styles.userMenuItem} onClick={handleRefresh}>
          <RefreshCw size={16} />
          Atualizar conversas
        </button>
      )}
      {isBusinessChannel && (
        <button style={styles.userMenuItem} onClick={() => goToPage('templates')}>
          <ScrollText size={16} />
          Templates da Meta
        </button>
      )}

      <div style={styles.userMenuDivider} />
      <button
        style={{ ...styles.userMenuItem, ...styles.userMenuItemDanger }}
        onClick={handleLogout}
      >
        <LogOut size={16} />
        Sair
      </button>
    </div>
  );

  const renderAvatar = () => (
    <div
      style={{
        ...styles.userAvatar,
        ...(showUserMenu ? styles.userAvatarActive : {}),
        ...(isDelegatedSession ? { borderColor: '#f59e0b' } : {}),
      }}
      onClick={() => setShowUserMenu(!showUserMenu)}
      title={isDelegatedSession
        ? `No workspace de ${delegation?.targetName || 'outro usuário'}`
        : undefined}
    >
      <User size={18} color={isDelegatedSession ? '#f59e0b' : undefined} />
    </div>
  );

  // Modal da conta do Instagram: mora aqui (e não nos shells) para o item do
  // menu funcionar em QUALQUER página que renderize o NavSidebar. Depois de
  // desconectar, o inbox do IG precisa voltar ao estado "conectar" — reload é
  // o caminho simples; nas demais páginas basta atualizar o cache.
  const igAccountModal = showIgAccount ? (
    <InstagramAccountModal
      onClose={() => setShowIgAccount(false)}
      onDisconnected={() => {
        publishInstagramStatus({ ...(igStatus || {}), connected: false });
        setShowIgAccount(false);
        if (location.pathname.startsWith('/instagram')) {
          window.location.reload();
        }
      }}
      onRequestConnect={() => {
        setShowIgAccount(false);
        navigate('/instagram', { state: { openConnection: true } });
      }}
    />
  ) : null;

  // Modal da Pagina do Messenger: mesmo racional do modal do Instagram.
  const msAccountModal = showMsAccount ? (
    <MessengerAccountModal
      onClose={() => setShowMsAccount(false)}
      onDisconnected={() => {
        const data = { ...(msStatus || {}), connected: false };
        msStatusCache = { data };
        setMsStatus(data);
        setShowMsAccount(false);
        if (location.pathname.startsWith('/messenger')) {
          window.location.reload();
        }
      }}
      onRequestConnect={() => {
        setShowMsAccount(false);
        navigate('/messenger', { state: { openConnection: true } });
      }}
    />
  ) : null;

  // ---- MOBILE: barra SUPERIOR (perfil + marca + sino) e barra INFERIOR (abas) ----
  if (isMobile) {
    return (
      <>
        <div style={{ ...styles.mobileTopBar, display: mobileHideNav ? 'none' : 'flex' }}>
          <div style={{ position: 'relative' }} className="nav-user-container">
            {renderAvatar()}
            {showUserMenu && renderUserMenu()}
          </div>
          <div style={styles.brand}>
            <span style={styles.brandLogo}>
              <img
                src={process.env.PUBLIC_URL + '/logo.jpeg'}
                alt="InfinitSales"
                style={{ width: 18, height: 18, objectFit: 'contain' }}
              />
            </span>
            <span style={styles.brandText}>InfinitSales</span>
          </div>
          <NotificationBell />
        </div>

        <div style={styles.container}>
          {bottomItems.map((item) => renderNavButton(item))}
        </div>
        {igAccountModal}
        {msAccountModal}
      </>
    );
  }

  // ---- DESKTOP: trilho lateral de 68px ----
  return (
    <div style={styles.container}>
      <nav style={styles.nav}>
        {navItems.map((item) => (
          <React.Fragment key={item.id}>
            {renderNavButton(item)}
            {item.id === 'empresa' && <div style={styles.divider} />}
          </React.Fragment>
        ))}
      </nav>

      <div style={styles.footer}>
        <div style={styles.divider} />

        {/* Toggle de tema — sozinho no rodapé do trilho (o sino migrou para a
            barra superior do inbox). */}
        <button
          style={{
            ...styles.footerBtn,
            ...(hoveredItem === 'theme' ? styles.navItemHover : {}),
          }}
          onClick={handleToggleTheme}
          onMouseEnter={() => setHoveredItem('theme')}
          onMouseLeave={() => setHoveredItem(null)}
        >
          {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          {hoveredItem === 'theme' && (
            <div style={styles.tooltip}>
              {theme === 'dark' ? 'Modo claro' : 'Modo escuro'}
            </div>
          )}
        </button>

        <div style={{ position: 'relative' }} className="nav-user-container">
          {renderAvatar()}
          {showUserMenu && renderUserMenu()}
        </div>
      </div>
      {igAccountModal}
      {msAccountModal}
    </div>
  );
};

export default NavSidebar;